import Action from 'ageas-react-component-action'
import Dropdown from 'ageas-react-component-dropdownlist'
import Link from 'ageas-react-component-link'
import MiniLoader from 'ageas-react-component-miniloader'
import TextField from 'ageas-react-component-textfield'

import { useEffect, useState } from 'react'
import { useDigitalSignatureSettingsStore } from '../../Stores/digitalSignature/useDigitalSignatureSettingsStore'
import { useGetAdditionalEntityDetailsStore } from '../../Stores/useGetAdditionalEntityDetailsStore'
import { useChangeDigitalSignatureProcess } from '../../hooks/api/changeDigitalSignatureProcess/useChangeDigitalSignatureProcess'
import { useChangeDigitalSignatureProcessPayload } from '../../hooks/api/changeDigitalSignatureProcess/useChangeDigitalSignatureProcessPayload'
import { useResendDigitalSignature } from '../../hooks/api/resendDigitalSignature/useResendDigitalSignature'
import { useResendDigitalSignaturePayload } from '../../hooks/api/resendDigitalSignature/useResendDigitalSignaturePayload'
import { t } from '../../i18n'
import { validateEmail, validatePhoneNumber } from '../../utils/validates'
import { Cell, Row } from '../AnimalInsuranceTable/styles'
import {
  ActionButtonContainer,
  ButtonsContainer,
  ExpandedLoading,
} from './styles'
import { usePayloadRefreshDigitalSignatureStatus } from './usePayloadRefreshDigitalSignatureStatus'
import { usePayloadSendDIgitalSignature } from './usePayloadSendDIgitalSignature'

type Props = {
  entityPhone: string
}

export const EMAIL_OPTION_CODE = 'OTP' as const

export const Expanded = ({ entityPhone }: Props) => {
  const {
    settings,
    OTPData,
    isSendSignature,
    setOPTData,
    isChangedDigitalSignature,
    signatureTypeSelected,
    setSignatureTypeSelected,
    isInitialSignatureClicked,
    setIsInitialSignatureClicked,
    setIsChangedDigitalSignature,
  } = useDigitalSignatureSettingsStore()
  const { policyholderAdditionalDetails } = useGetAdditionalEntityDetailsStore()
  const [isEmailPristine, setIsEmailPristine] = useState(false)
  const [isPhonePristine, setIsPhonePristine] = useState(false)
  const { handleChangeDigitalSignatureProcessPayload } =
    useChangeDigitalSignatureProcessPayload()

  const [email, setEmail] = useState(
    OTPData?.email || policyholderAdditionalDetails?.emailContact?.email || '',
  )
  const [phone, setPhone] = useState(
    OTPData?.phone ||
      entityPhone ||
      policyholderAdditionalDetails?.phoneContactList?.[0]?.phoneNumber ||
      '',
  )

  const {
    handleSendDigitalSignature,
    isLoadingSendDigitalSignature,
    isSuccessSendDigitalSignature,
    handleSignWithDigitalDeviceMutate,
    isLoadingSignWithDigitalDevice,
  } = usePayloadSendDIgitalSignature({
    email,
    phone,
  })

  const resendDigitalSignaturePayload = useResendDigitalSignaturePayload()
  const { mutate: resendDigitalSignatureMutate } = useResendDigitalSignature()

  const { handleRefreshDigitalSignature, isLoadingRefresh } =
    usePayloadRefreshDigitalSignatureStatus()

  const formattedSignatureType = settings?.signatureSettings?.map((item) => ({
    caption: item.signatureDescription,
    value: item.signatureType,
  }))

  const { mutate: mutateChange, isLoading: isLoadingChange } =
    useChangeDigitalSignatureProcess()

  const handleChangeDigitalSignature = () => {
    mutateChange(handleChangeDigitalSignatureProcessPayload())
  }

  const handleSignatureTypeSelected = (value: string) => {
    const digitalSignatureType = settings?.signatureSettings?.find(
      (item) => item.signatureType === value,
    )
    setSignatureTypeSelected(digitalSignatureType as never)
  }

  const emailMessage = () => {
    const emailLength = email.length

    if (emailLength > 0) {
      if (!validateEmail(email)) {
        return t('pages.step7.otp.invalidEmail')
      }
    } else if (emailLength === 0 && isEmailPristine) {
      return t('pages.step7.mandatory')
    }

    return ''
  }

  const phoneNumberMessage = () => {
    const phoneNumberLength = phone.length

    if (phoneNumberLength > 0) {
      if (!validatePhoneNumber(phone, true)) {
        return t('pages.step7.otp.invalidPhoneNumber')
      }
    } else if (phoneNumberLength === 0 && isPhonePristine) {
      return t('pages.step7.mandatory')
    }

    return ''
  }

  useEffect(() => {
    setOPTData({
      email,
      phone,
    })
  }, [email, phone, setOPTData])

  return (
    <>
      {(isLoadingSendDigitalSignature ||
        isLoadingSignWithDigitalDevice ||
        isLoadingChange) && (
        <ExpandedLoading>
          <MiniLoader color="transparent" minHeight="auto" maxWidth="auto" />
        </ExpandedLoading>
      )}
      <Row>
        <Cell visible colSpan={6}>
          <div className="container">
            <div className="row">
              <div className="col">
                <Dropdown
                  noSort={true}
                  id={`confirmation-select-digital-signature-${signatureTypeSelected?.signatureType}`}
                  key={`confirmation-select-digital-signature-${signatureTypeSelected?.signatureType}`}
                  name="confirmation-select-digital-signature"
                  label={t('pages.step7.signatureType')}
                  value={signatureTypeSelected?.signatureDescription}
                  colorTheme="blue"
                  version="2"
                  options={formattedSignatureType}
                  onValueChanged={(id: string, value: string) => {
                    handleSignatureTypeSelected(value)

                    if (isInitialSignatureClicked || isSendSignature) {
                      setIsChangedDigitalSignature(true)
                    }
                  }}
                />
                {signatureTypeSelected?.requiredPhoneNumber && (
                  <TextField
                    required
                    id={'confirmation-policyholder-phone-contact'}
                    name="confirmation-policyholder-phone-contact"
                    label={t('constants.phoneTypes.phone')}
                    message={phoneNumberMessage()}
                    value={phone}
                    onValueChanged={(id: string, value: string) => {
                      setPhone(value)
                      setIsEmailPristine(true)

                      if (isInitialSignatureClicked || isSendSignature) {
                        setIsChangedDigitalSignature(true)
                      }
                    }}
                    colorTheme="blue"
                    maxLength={9}
                    mask="000000000"
                    version="2"
                  />
                )}
                {!isChangedDigitalSignature &&
                  (isSuccessSendDigitalSignature || isSendSignature) &&
                  signatureTypeSelected?.signatureType ===
                    EMAIL_OPTION_CODE && (
                    <Link
                      label={t('pages.step7.resendEmail')}
                      noPaddingTopBottom={true}
                      onValueChanged={() =>
                        resendDigitalSignatureMutate(
                          resendDigitalSignaturePayload,
                        )
                      }
                    />
                  )}
              </div>
              <div className="col">
                {signatureTypeSelected?.requiredEmail && (
                  <TextField
                    required
                    id={'confirmation-policyholder-email-contact'}
                    name="confirmation-policyholder-email-contact"
                    label={t('pages.step7.email')}
                    message={emailMessage()}
                    value={email}
                    onValueChanged={(id: string, value: string) => {
                      setEmail(value)
                      setIsPhonePristine(true)

                      if (isInitialSignatureClicked || isSendSignature) {
                        setIsChangedDigitalSignature(true)
                      }
                    }}
                    colorTheme="blue"
                    maxLength={70}
                    version="2"
                  />
                )}
                <Cell />

                <ActionButtonContainer>
                  {!isChangedDigitalSignature &&
                  (isSuccessSendDigitalSignature || isSendSignature) ? (
                    <ButtonsContainer>
                      {signatureTypeSelected?.signatureType ===
                      EMAIL_OPTION_CODE ? (
                        <div />
                      ) : (
                        <Action
                          id="digitalSignaturesButton"
                          label={t('pages.step7.signature')}
                          type="button"
                          disable={isLoadingRefresh}
                          onValueChanged={() => {
                            setIsInitialSignatureClicked(true)
                            handleSignWithDigitalDeviceMutate()
                          }}
                        />
                      )}
                      {isInitialSignatureClicked && (
                        <Action
                          id="digitalSignaturesButton"
                          label={t('pages.step7.updateSignatureStatus')}
                          buttonType="secondary"
                          type="button"
                          disable={isLoadingRefresh}
                          onValueChanged={handleRefreshDigitalSignature}
                        />
                      )}
                    </ButtonsContainer>
                  ) : null}

                  {isSendSignature && isChangedDigitalSignature && (
                    <ButtonsContainer>
                      <Link
                        label={t('pages.step7.changeConfirm')}
                        noPaddingTopBottom={true}
                        onValueChanged={handleChangeDigitalSignature}
                      />
                    </ButtonsContainer>
                  )}

                  {signatureTypeSelected && !isSendSignature ? (
                    <Action
                      id="digitalSignaturesButton"
                      label={
                        signatureTypeSelected?.signatureType ===
                        EMAIL_OPTION_CODE
                          ? t('pages.step7.sendSignature')
                          : t('pages.step7.startSignature')
                      }
                      buttonType="primary"
                      type="button"
                      disable={
                        isLoadingSendDigitalSignature ||
                        signatureTypeSelected === null ||
                        signatureTypeSelected === undefined ||
                        (emailMessage().length > 0 &&
                          phoneNumberMessage().length > 0) ||
                        (signatureTypeSelected?.signatureType ===
                          EMAIL_OPTION_CODE &&
                          email.length === 0 &&
                          phone.length === 0)
                      }
                      onValueChanged={() => {
                        if (
                          signatureTypeSelected?.signatureType ===
                          EMAIL_OPTION_CODE
                        ) {
                          setIsInitialSignatureClicked(true)
                        }
                        handleSendDigitalSignature()
                      }}
                    />
                  ) : null}
                </ActionButtonContainer>
              </div>
            </div>
          </div>
        </Cell>
      </Row>
    </>
  )
}
